import * as React from 'react';
import {
  motion,
  useMotionValue,
  Reorder,
  useDragControls
} from 'framer-motion';
import { useRaisedShadow } from './useRaisedShadow';
import ArgonTypography from 'components/ArgonTypography';
import PropTypes from 'prop-types';
import ClearIcon from '@mui/icons-material/Clear';
import ArgonBox from 'components/ArgonBox';
import { ReorderIcon } from './Icon';

import 'assets/css/reorderWorkouts.css';

// when onItemClicked is not null, then the item is clickable
// when onItemDeleted is not null, then the item is deletable and will show a delete icon

export const ReorderItem = ({
  item,
  onItemClicked,
  onItemDeleted,
  listItemComponent
}) => {
  const dragControls = useDragControls();
  const y = useMotionValue(0);
  const boxShadow = useRaisedShadow(y);

  const handleItemClicked = (item) => {
    // Only call onItemClicked if it's a function
    console.log('ReorderItem: handleItemClicked called', item);
    if (onItemClicked && typeof onItemClicked === 'function') {
      console.log('ReorderItem: calling parent onItemClicked');
      onItemClicked(item);
    }
  };

  const renderCustomItem = () => {
    console.log('ReorderItem: rendering custom item');
    if (listItemComponent) {
      if (React.isValidElement(listItemComponent)) {
        return React.cloneElement(listItemComponent, {
          item,
          onItemClicked: handleItemClicked
        });
      } else {
        // If it's a component reference, create an element from it
        const Component = listItemComponent;
        return <Component item={item} onItemClicked={handleItemClicked} />;
      }
    }

    return (
      <ArgonTypography
        onClick={() => handleItemClicked(item)}
        style={{
          cursor: onItemClicked ? 'pointer' : 'default',
          width: '100%'
        }}
        variant="caption"
        fontWeight="bold">
        <ArgonBox display="flex" alignItems="center" gap={2} m={0} p={0}>
          {item?.color && (
            <div
              style={{
                backgroundColor: item?.color,
                width: '16px',
                height: '16px',
                borderRadius: '4px'
                // border: '1px solid #00FF00'
              }}
            />
          )}
          {item?.label}
        </ArgonBox>
      </ArgonTypography>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.75 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0 }}
      transition={{ duration: 0.15 }}>
      <Reorder.Item
        value={item}
        id={item.id}
        className="reorderItem"
        style={{
          boxShadow,
          y,
          display: 'flex',
          alignItems: 'flex-start',
          padding: '8px 16px',
          backgroundColor: 'white',
          borderRadius: '8px',
          marginBottom: '8px',
          // cursor: 'grab',
          border: '1px solid #e0e0e0'
        }}
        dragListener={false}
        dragControls={dragControls}>
        {renderCustomItem()}
        <ArgonBox
          display="flex"
          flexDirection="row"
          alignItems="flex-end"
          mt={0.5}>
          {onItemDeleted && (
            <ArgonBox ml={2}>
              <ClearIcon
                fontSize="small"
                onClick={onItemDeleted}
                sx={{ cursor: 'pointer', color: 'grey' }}
              />
            </ArgonBox>
          )}
          <ArgonBox ml={2}>
            <ReorderIcon dragControls={dragControls} />
          </ArgonBox>
        </ArgonBox>
      </Reorder.Item>
    </motion.div>
  );
};

ReorderItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    color: PropTypes.string
  }).isRequired,
  onItemClicked: PropTypes.func,
  onItemDeleted: PropTypes.func,
  listItemComponent: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType
  ])
};
